import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { useDebounce } from 'react-use';
import cn from '../utils/cn';
import { zIndex } from '../../z-index';
import KoobInputLabel from './KoobInputLabel';
import KoobInputText from './KoobInputText';
import KoobLoader from './KoobLoader';
import { optionVariants } from './KoobInputSelect';
import useDismissOnOutsidePointerDown from '../hooks/useDismissOnOutsidePointerDown';

// ponytail: holds open/query/results state instead of being stateless, and
// owns the debounce — same deviation (and same reason) as the note on
// KoobInputSelect: Radix has no combobox primitive, and pushing the debounce
// to the call site is exactly the duplication this component exists to kill.
// The fetch itself stays injected, so `components/` still makes no API calls.

export interface KoobLocationValue {
  id: string | number;
  title: string;
  /** 'city' | 'country' | 'region' | 'hotel' | … — or their plural variants. */
  kind?: string;
  description?: string;
}

/** Paginated `fetchResults` return — a bare array opts out of pagination. */
export interface KoobLocationPage {
  items: KoobLocationValue[];
  /** Another page exists for the same query. */
  hasMore?: boolean;
}

const DEFAULT_MIN_LENGTH = 2;
const DEFAULT_DEBOUNCE_MS = 500;
// Start fetching the next page slightly before the sentinel is on screen, so
// the rows are usually there by the time the user scrolls onto them.
const LOAD_MORE_ROOT_MARGIN = '80px';

const toPage = (
  result: KoobLocationValue[] | KoobLocationPage,
): KoobLocationPage =>
  Array.isArray(result)
    ? { items: result, hasMore: false }
    : { items: result?.items ?? [], hasMore: Boolean(result?.hasMore) };

const identity = (item: KoobLocationValue) => `${item.kind ?? ''}:${item.id}`;

// The section key of `suggestions`: no result kind is spelled like it.
const SUGGESTED = '__suggested';
const NONE: KoobLocationValue[] = [];

type KoobLocationSearchProps = {
  /**
   * Legacy call sites can hold `''` where they mean "nothing selected", so
   * anything that isn't an object is normalised to null.
   */
  value?: KoobLocationValue | null | '';
  onValueChange: (value: KoobLocationValue | null) => void;
  /**
   * Injected — `components/` may not call an API itself. Receives the 1-based
   * page; return `{ items, hasMore }` to let the panel fetch the rest as the
   * user scrolls, or a bare array to keep the single-shot behaviour.
   */
  fetchResults: (
    query: string,
    page: number,
  ) => Promise<KoobLocationValue[] | KoobLocationPage>;
  /** Kind order for the result sections. Unlisted kinds sort last. */
  groupOrder?: string[];
  /** Kind → section heading. Falls back to the raw kind. */
  groupLabels?: Record<string, React.ReactNode>;
  minLength?: number;
  debounceMs?: number;
  /**
   * Reset the field after a pick instead of showing the selection — for
   * accumulator inputs whose picks become pills elsewhere.
   */
  clearOnSelect?: boolean;
  /** Shown while the query is shorter than `minLength`. */
  hint?: React.ReactNode;
  /**
   * Offered instead of `hint` while the query is shorter than `minLength`,
   * under `suggestionsLabel`: picking one works like picking a result.
   */
  suggestions?: KoobLocationValue[];
  suggestionsLabel?: React.ReactNode;
  /** Shown when a long-enough query returned nothing. */
  emptyState?: React.ReactNode;
  placeholder?: string;
  /** Left icon; a magnifying glass by default. */
  icon?: React.ReactNode;
  label?: React.ReactNode;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  size?: 'sm' | 'md';
  rounded?: 'md' | 'full';
  name?: string;
  'data-testid'?: string;
  /** Not part of the API (see packages/ui/README.md). */
  className?: never;
};

const KoobLocationSearch = React.forwardRef<
  HTMLDivElement,
  KoobLocationSearchProps
>(
  (
    {
      value,
      onValueChange,
      fetchResults,
      groupOrder,
      groupLabels,
      minLength = DEFAULT_MIN_LENGTH,
      debounceMs = DEFAULT_DEBOUNCE_MS,
      clearOnSelect = false,
      hint,
      suggestions,
      suggestionsLabel,
      emptyState,
      placeholder,
      icon = <i className="fa-regular fa-magnifying-glass" aria-hidden />,
      label,
      required = false,
      disabled = false,
      error = false,
      size = 'md',
      rounded,
      name,
      'data-testid': dataCy,
      // className is not part of the API (see packages/ui/README.md) but is
      // still stripped so a stray one can't clobber the computed classes
      className,
      ...rest
    },
    forwardedRef,
  ) => {
    const [open, setOpen] = useState(false);
    const [term, setTerm] = useState('');
    const [results, setResults] = useState<KoobLocationValue[]>([]);
    // The query `results` actually belongs to. Anything else means a search is
    // pending — either still inside the debounce window or in flight. Deriving
    // it beats a `loading` flag set in the debounced callback, which leaves a
    // gap where the query is valid, results are empty, and nothing is loading
    // yet — the panel flashed "No results found" before the spinner.
    const [settledQuery, setSettledQuery] = useState('');
    // Has the user typed since the field was focused? Until they do, an open
    // field keeps showing its current selection instead of blanking out, and
    // no search runs for a query the user never asked for.
    const [dirty, setDirty] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    // Pagination state for `settledQuery`: the last page appended to `results`,
    // and whether the source says there is another one after it.
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);

    const triggerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    // Read in the observer callback so a load-more triggered while one is
    // already in flight is dropped — state alone lags behind the callback.
    const loadingMoreRef = useRef(false);
    // The query the latest in-flight request was made for, so a slow response
    // for an old query can't overwrite a fast one for the current query.
    const latestQuery = useRef('');
    // Read through a ref so an inline arrow at the call site doesn't have to
    // be memoised to avoid re-firing the search.
    const fetchRef = useRef(fetchResults);
    fetchRef.current = fetchResults;

    // `''` and undefined both mean "nothing selected" — see the prop doc.
    const selected =
      value && typeof value === 'object' && value.id != null ? value : null;

    const query = dirty ? term.trim() : '';
    const isQueryValid = query.length >= minLength;

    const isPending = isQueryValid && query !== settledQuery;
    const suggesting = !isQueryValid && Boolean(suggestions?.length);
    const shown = isQueryValid ? results : suggesting ? suggestions! : NONE;

    useDebounce(
      () => {
        latestQuery.current = query;
        // A new query restarts the paging: page 1 replaces `results` instead of
        // appending, and the previous query's `hasMore` must not survive it.
        setPage(1);
        setHasMore(false);
        if (!isQueryValid) {
          setResults([]);
          return;
        }
        fetchRef
          .current(query, 1)
          .then((result) => {
            if (latestQuery.current !== query) return;
            const first = toPage(result);
            setResults(first.items);
            setHasMore(Boolean(first.hasMore));
            setSettledQuery(query);
          })
          .catch(() => {
            if (latestQuery.current !== query) return;
            setResults([]);
            setSettledQuery(query);
          });
      },
      debounceMs,
      [query, isQueryValid],
    );

    const loadMore = () => {
      if (loadingMoreRef.current || !hasMore) return;
      const nextPage = page + 1;
      // Captured, not read from the ref at resolve time: a response for a query
      // the user has already moved on from must not be appended.
      const pagedQuery = query;
      loadingMoreRef.current = true;
      setLoadingMore(true);
      fetchRef
        .current(pagedQuery, nextPage)
        .then((result) => {
          if (latestQuery.current !== pagedQuery) return;
          const next = toPage(result);
          // Pages are sliced from one ranked list, so an index update between
          // two requests can repeat a row across pages — drop the duplicates
          // rather than render two identical (and same-keyed) options.
          setResults((prev) => {
            const seen = new Set(prev.map(identity));
            return [
              ...prev,
              ...next.items.filter((i) => !seen.has(identity(i))),
            ];
          });
          setHasMore(Boolean(next.hasMore));
          setPage(nextPage);
        })
        .catch(() => {
          if (latestQuery.current !== pagedQuery) return;
          // Stop here rather than let the sentinel retry the failing page in a
          // loop; what was already fetched stays usable.
          setHasMore(false);
        })
        .finally(() => {
          loadingMoreRef.current = false;
          setLoadingMore(false);
        });
    };
    const loadMoreRef = useRef(loadMore);
    loadMoreRef.current = loadMore;

    // The sentinel lives in a portal that only exists while the panel is open,
    // so it is tracked as state (a callback ref re-running on mount) rather
    // than a plain ref the effect would read too early.
    const [sentinel, setSentinel] = useState<HTMLDivElement | null>(null);

    // Re-created after every page: an IntersectionObserver only fires on
    // transitions, and the sentinel commonly stays on screen after a load —
    // a page whose rows the call site filtered out, or a panel taller than its
    // content. Observing afresh re-evaluates it and keeps the chain going.
    useEffect(() => {
      if (!sentinel) return;
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            loadMoreRef.current();
          }
        },
        { root: scrollRef.current, rootMargin: LOAD_MORE_ROOT_MARGIN },
      );
      observer.observe(sentinel);
      return () => observer.disconnect();
    }, [sentinel, page, loadingMore]);

    // Results bucketed by kind, sections ordered by `groupOrder`. `flat` is the
    // same rows in render order — keyboard navigation runs on it so the arrows
    // cross section boundaries without needing to know about them. `flatIndex`
    // is that order as a lookup: paging accumulates rows, and one indexOf per
    // rendered row is quadratic in a list that no longer stops at 20.
    // Suggestions are one section of their own, whatever their kinds.
    const { groups, flat, flatIndex } = useMemo(() => {
      const buckets = new Map<string, KoobLocationValue[]>();
      shown.forEach((item) => {
        const kind = suggesting ? SUGGESTED : (item.kind ?? '');
        const bucket = buckets.get(kind);
        if (bucket) bucket.push(item);
        else buckets.set(kind, [item]);
      });
      const rank = (kind: string) => {
        const index = groupOrder?.indexOf(kind) ?? -1;
        return index === -1 ? Number.MAX_SAFE_INTEGER : index;
      };
      const ordered = [...buckets.entries()].sort(
        (a, b) => rank(a[0]) - rank(b[0]),
      );
      const flattened = ordered.flatMap(([, items]) => items);
      return {
        groups: ordered,
        flat: flattened,
        flatIndex: new Map(flattened.map((item, index) => [item, index])),
      };
    }, [shown, suggesting, groupOrder]);

    const close = () => {
      setOpen(false);
      setFocusedIndex(-1);
      setTerm('');
      setDirty(false);
    };

    useDismissOnOutsidePointerDown(open, close, [triggerRef, contentRef]);

    const handleSelect = (item: KoobLocationValue) => {
      onValueChange(item);
      setOpen(false);
      setFocusedIndex(-1);
      setTerm('');
      setDirty(false);
    };

    const handleClear = () => {
      setTerm('');
      setDirty(false);
      setResults([]);
      setPage(1);
      setHasMore(false);
      setFocusedIndex(-1);
      onValueChange(null);
      inputRef.current?.focus();
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          event.stopPropagation();
          setOpen(true);
          // At the last row with pages left, fetch instead of wrapping around:
          // keyboard users would otherwise never reach what the scroll sentinel
          // loads for everyone else.
          if (focusedIndex >= flat.length - 1 && hasMore) {
            loadMoreRef.current();
            break;
          }
          setFocusedIndex((prev) => (prev < flat.length - 1 ? prev + 1 : 0));
          break;
        case 'ArrowUp':
          event.preventDefault();
          event.stopPropagation();
          setFocusedIndex((prev) => (prev > 0 ? prev - 1 : flat.length - 1));
          break;
        case 'Enter':
          // Only ours when a row is highlighted — otherwise Enter belongs to
          // the host (the trip wizard advances a step with it).
          if (open && flat[focusedIndex]) {
            event.preventDefault();
            event.stopPropagation();
            handleSelect(flat[focusedIndex]);
          }
          break;
        case 'Escape':
          if (open) {
            event.stopPropagation();
            close();
          }
          break;
      }
    };

    // A pick is shown in the field itself (that is the whole point of this
    // component), so the field reads as the live query only once the user
    // actually types — focusing a field that already holds "Paris" must not
    // blank it out. The text is selected on focus, so typing still replaces.
    // `clearOnSelect` call sites never show a selection — their picks live in
    // pills elsewhere.
    const displayValue = clearOnSelect
      ? term
      : dirty
        ? term
        : (selected?.title ?? '');
    const hasClearable = Boolean(selected || term);

    const panel = (
      <div
        ref={scrollRef}
        className="overflow-y-auto p-1"
        // Same rule as KoobInputSelect: the list shrinks to the room left on
        // the chosen side rather than running past the fold on a short screen.
        style={{
          maxHeight:
            'min(18rem, calc(var(--radix-popover-content-available-height, 100vh) - 1rem))',
        }}
        data-testid={dataCy}
      >
        {!isQueryValid && !suggesting ? (
          <div className="py-6 text-center text-sm text-gray-500">
            {hint ?? `Type at least ${minLength} characters to search`}
          </div>
        ) : isPending ? (
          <div className="flex justify-center py-6">
            <KoobLoader size={16} />
          </div>
        ) : // Nothing to show *yet* is not the same as nothing to show: a page
        // whose rows the call site filtered out leaves the list empty with more
        // still to come, and the sentinel below is about to fetch it.
        flat.length === 0 && !hasMore ? (
          <div className="py-6 text-center text-sm text-gray-500">
            {emptyState ?? 'No results found'}
          </div>
        ) : (
          <ul role="listbox" className="m-0 list-none p-0">
            {groups.map(([kind, items]) => {
              const heading =
                kind === SUGGESTED
                  ? (suggestionsLabel ?? 'Suggestions')
                  : (groupLabels?.[kind] ?? kind);
              return (
                <li key={kind || 'other'} role="presentation">
                  {kind && (
                    <div className="px-2 pb-1 pt-2 text-xs font-semibold uppercase tracking-wider text-gray-400">
                      {heading}
                    </div>
                  )}
                  <ul
                    role="group"
                    aria-label={typeof heading === 'string' ? heading : kind}
                    className="m-0 list-none p-0"
                  >
                    {items.map((item) => {
                      const index = flatIndex.get(item) ?? -1;
                      const isSelected = selected?.id === item.id;
                      const isFocused = index === focusedIndex;
                      return (
                        <li
                          key={`${kind}-${item.id}`}
                          role="option"
                          aria-selected={isSelected}
                          data-testid={item.title}
                          onClick={() => handleSelect(item)}
                          className={optionVariants({
                            size,
                            state:
                              isSelected && isFocused
                                ? 'selectedFocused'
                                : isSelected
                                  ? 'selected'
                                  : isFocused
                                    ? 'focused'
                                    : 'default',
                          })}
                        >
                          <span className="min-w-0 flex-1">
                            <span className="block truncate">{item.title}</span>
                            {item.description && (
                              <span className="block truncate text-xs italic text-gray-500">
                                {item.description}
                              </span>
                            )}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              );
            })}
          </ul>
        )}

        {isQueryValid && !isPending && hasMore && (
          <div
            ref={setSentinel}
            className="flex justify-center py-3"
            aria-hidden
          >
            <KoobLoader size={16} />
          </div>
        )}
      </div>
    );

    const control = (
      <Popover.Root
        open={open}
        onOpenChange={(next) => {
          if (next && disabled) return;
          if (next) setOpen(true);
          else close();
        }}
      >
        <div
          translate="no"
          className="notranslate relative w-full"
          ref={forwardedRef}
          {...rest}
        >
          {/* The anchor has to be a real box: the popover matches the field's
              width via --radix-popover-trigger-width, which is measured from
              it. It doubles as the "inside" element for the dismiss hook. */}
          <Popover.Anchor asChild>
            <div className="w-full" ref={triggerRef}>
              <KoobInputText
                ref={inputRef}
                name={name}
                size={size}
                rounded={rounded}
                error={error}
                disabled={disabled}
                placeholder={placeholder}
                value={displayValue}
                role="combobox"
                aria-expanded={open}
                autoComplete="off"
                iconLeft={icon}
                iconRight={
                  hasClearable ? (
                    <i className="fa-regular fa-xmark" aria-hidden />
                  ) : undefined
                }
                onIconRightClick={hasClearable ? handleClear : undefined}
                onFocus={(event) => {
                  setOpen(true);
                  setDirty(false);
                  // Selected, not cleared: the current pick stays readable and
                  // the first keystroke still replaces it. Covers keyboard
                  // focus; a click is handled by onMouseUp below.
                  event.target.select();
                }}
                // A click fires focus *before* mouseup, and mouseup collapses
                // the selection — and clicking an already-focused field fires
                // no focus event at all. Re-select here until the user starts
                // typing, after which clicks place the caret as normal.
                onMouseUp={(event) => {
                  if (!dirty) event.currentTarget.select();
                }}
                onKeyDown={handleKeyDown}
                onChange={(event) => {
                  setTerm(event.target.value);
                  setDirty(true);
                  setFocusedIndex(-1);
                  setOpen(true);
                }}
              />
            </div>
          </Popover.Anchor>

          <Popover.Portal>
            <Popover.Content
              ref={contentRef}
              translate="no"
              align="start"
              sideOffset={4}
              collisionPadding={8}
              // Keep focus in the field so the user can keep typing.
              onOpenAutoFocus={(event) => event.preventDefault()}
              // The field is an Anchor, not a Trigger, so Radix counts typing
              // in it as an interaction *outside* the content and dismisses on
              // the spot. Dismissal is ours (useDismissOnOutsidePointerDown).
              onInteractOutside={(event) => event.preventDefault()}
              style={{
                width: 'var(--radix-popover-trigger-width)',
                // The open menu must paint above any modal it was triggered
                // from — see the scale in packages/ui/z-index.js.
                zIndex: zIndex.popover,
              }}
              className={cn(
                'notranslate overflow-hidden rounded-[10px] border border-solid border-k-gray bg-white shadow-[0_10px_30px_rgba(27,44,73,.12)]',
              )}
            >
              {panel}
            </Popover.Content>
          </Popover.Portal>
        </div>
      </Popover.Root>
    );

    return label != null ? (
      <KoobInputLabel
        label={label}
        required={required}
        size={size === 'md' ? 'default' : size}
        disabled={disabled}
      >
        {control}
      </KoobInputLabel>
    ) : (
      control
    );
  },
);

KoobLocationSearch.displayName = 'KoobLocationSearch';

export default KoobLocationSearch;
